// Crystallisation Dialog - Process annual crystallisation for series
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Calculate,
  ExpandMore,
  Info,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { Series } from '../../types';
import {
  calculateCrystallisation,
  executeCrystallisation,
  getScenarioExplanation,
  CrystallisationCalculation,
} from '../../services/crystallisationService';
import { formatCurrency, formatPercentage } from '../../utils/navCalculations';

interface CrystallisationDialogProps {
  open: boolean;
  series: Series[];
  onClose: () => void;
  onSuccess?: () => void;
}

const CrystallisationDialog: React.FC<CrystallisationDialogProps> = ({
  open,
  series,
  onClose,
  onSuccess,
}) => {
  const [selectedSeries, setSelectedSeries] = useState<Series | null>(null);
  const [calculation, setCalculation] = useState<CrystallisationCalculation | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'capitalised' | 'cash_payment'>('capitalised');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [crystallisationDate, setCrystallisationDate] = useState<Date>(
    new Date(new Date().getFullYear(), 11, 31) // Dec 31st of current year
  );

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedSeries(null);
      setCalculation(null);
      setPaymentMethod('capitalised');
      setError('');
      setSuccess(false);
    }
  }, [open]);

  const handleCalculate = async (series: Series) => {
    setLoading(true);
    setError('');
    try {
      const calc = await calculateCrystallisation(series, crystallisationDate);
      setCalculation(calc);
      setSelectedSeries(series);
    } catch (err: any) {
      setError(err.message || 'Failed to calculate crystallisation');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!calculation) return;

    setLoading(true);
    setError('');

    try {
      await executeCrystallisation(calculation, paymentMethod);
      setSuccess(true);
      setTimeout(() => {
        if (onSuccess) onSuccess();
        handleClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to execute crystallisation');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedSeries(null);
      setCalculation(null);
      setPaymentMethod('capitalised');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  const getScenarioColor = (scenario: CrystallisationCalculation['scenario']) => {
    switch (scenario) {
      case 'full_performance_fee':
        return '#4CAF50'; // Green
      case 'below_hurdle':
      case 'underwater_recovery':
        return '#FFA500'; // Orange
      case 'loss':
        return '#F44336'; // Red
      default:
        return '#999';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1a1a',
          border: '1px solid #FFD700',
        }
      }}
    >
      <DialogTitle sx={{ color: '#FFD700', borderBottom: '1px solid #333' }}>
        Annual Crystallisation - December 31st
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
            Crystallisation executed successfully! High Water Mark updated.
          </Alert>
        )}

        {/* Series Selection */}
        {!calculation && !success && (
          <Box>
            <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
              Select Series to Crystallise
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
              Crystallisation Date: <strong style={{ color: '#FFD700' }}>{crystallisationDate.toLocaleDateString()}</strong>
            </Typography>

            {series.length === 0 ? (
              <Alert severity="info">No active series found</Alert>
            ) : (
              series.map((s) => (
                <Box
                  key={s.id}
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: '#0a0a0a',
                    border: '1px solid #333',
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#FFD700',
                      bgcolor: 'rgba(255, 215, 0, 0.05)',
                    }
                  }}
                  onClick={() => handleCalculate(s)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
                        Series #{s.id.substring(0, 8)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#999' }}>
                        Issued: {s.issueDate instanceof Date ? s.issueDate.toLocaleDateString() : new Date(s.issueDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ color: '#FFD700' }}>
                        {formatCurrency(s.currentNAV)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#999' }}>
                        Principal: {formatCurrency(s.principalAmount)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        )}

        {/* Calculation Results */}
        {calculation && !success && (
          <Box>
            <Button
              onClick={() => {
                setCalculation(null);
                setSelectedSeries(null);
              }}
              sx={{ mb: 2, color: 'white' }}
            >
              ← Back to Series Selection
            </Button>

            {/* Performance Summary */}
            <Box sx={{ p: 2, bgcolor: '#0a0a0a', borderRadius: 1, mb: 2, border: `2px solid ${getScenarioColor(calculation.scenario)}` }}>
              <Typography variant="h6" sx={{ color: '#FFD700', mb: 1 }}>
                Crystallisation Summary
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#999' }}>Performance Period</Typography>
                  <Typography sx={{ color: 'white' }}>
                    {calculation.performancePeriodStart.toLocaleDateString()} - {calculation.performancePeriodEnd.toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>({calculation.daysInPeriod} days)</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#999' }}>Scenario</Typography>
                  <Chip 
                    label={calculation.scenario.replace('_', ' ').toUpperCase()}
                    sx={{ bgcolor: getScenarioColor(calculation.scenario), color: 'white', mt: 0.5 }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2, bgcolor: '#333' }} />

              {/* NAV & Profit */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#999' }}>Starting NAV</Typography>
                  <Typography sx={{ color: 'white' }}>{formatCurrency(calculation.startNAV)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#999' }}>Ending NAV</Typography>
                  <Typography sx={{ color: '#FFD700', fontWeight: 'bold' }}>{formatCurrency(calculation.endNAV)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#999' }}>Gross Profit</Typography>
                  <Typography sx={{ color: calculation.grossProfit >= 0 ? '#4CAF50' : '#F44336' }}>
                    {calculation.grossProfit >= 0 ? '+' : ''}{formatCurrency(calculation.grossProfit)}
                    ({calculation.grossProfit >= 0 ? '+' : ''}{calculation.grossProfitPercent.toFixed(2)}%)
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#999' }}>Annualized Return</Typography>
                  <Typography sx={{ color: 'white' }}>
                    {calculation.annualisedReturnRate.toFixed(2)}%
                    {calculation.hurdleRateMet ? ' ✓' : ' ✗'} (Hurdle: 3%)
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2, bgcolor: '#333' }} />

              {/* High Water Mark */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#999' }}>High Water Mark</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ color: 'white' }}>
                    {formatCurrency(calculation.previousHighWaterMark)} → {formatCurrency(calculation.newHighWaterMark)}
                  </Typography>
                  {calculation.highWaterMarkExceeded ? (
                    <Chip label="Exceeded ✓" size="small" sx={{ bgcolor: '#4CAF50', color: 'white' }} />
                  ) : (
                    <Chip label="Not Exceeded" size="small" sx={{ bgcolor: '#666', color: 'white' }} />
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2, bgcolor: '#333' }} />

              {/* Fees & Interest */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#999' }}>Performance Fee (20%)</Typography>
                  <Typography sx={{ color: '#FFD700', fontWeight: 'bold', fontSize: '1.2rem' }}>
                    {formatCurrency(calculation.performanceFee)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#999' }}>Interest to Lender</Typography>
                  <Typography sx={{ color: '#4CAF50', fontWeight: 'bold', fontSize: '1.2rem' }}>
                    {formatCurrency(calculation.interest)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Scenario Explanation */}
            <Accordion sx={{ bgcolor: '#0a0a0a', border: '1px solid #333', mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#FFD700' }} />}>
                <Info sx={{ color: '#FFD700', mr: 1 }} />
                <Typography sx={{ color: '#FFD700' }}>Scenario Explanation</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: 'white', mb: 2 }}>
                  {getScenarioExplanation(calculation.scenario)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                  Per Loan Agreement Clause 3.2
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* Payment Method Selection */}
            <Box sx={{ p: 2, bgcolor: '#0a0a0a', borderRadius: 1, border: '1px solid #FFD700' }}>
              <Typography variant="h6" sx={{ color: '#FFD700', mb: 1 }}>
                Interest Payment Method
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }} icon={<Info />}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Contract Clause 3.3: Crystallisation and Payment
                </Typography>
                <Typography variant="body2">
                  "Unless the Lender provides written notice requesting cash payment at least fifteen (15) Business Days prior to a Crystallisation Date, the Interest shall be <strong>automatically capitalised</strong> and added to the NAV attributable to that Series."
                </Typography>
              </Alert>

              <FormControl component="fieldset">
                <RadioGroup
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as 'capitalised' | 'cash_payment')}
                >
                  <FormControlLabel
                    value="capitalised"
                    control={<Radio sx={{ color: '#FFD700', '&.Mui-checked': { color: '#FFD700' } }} />}
                    label={
                      <Box>
                        <Typography sx={{ color: 'white' }}>Capitalize Interest (Default)</Typography>
                        <Typography variant="caption" sx={{ color: '#999' }}>
                          Add {formatCurrency(calculation.interest)} to series NAV
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="cash_payment"
                    control={<Radio sx={{ color: '#FFD700', '&.Mui-checked': { color: '#FFD700' } }} />}
                    label={
                      <Box>
                        <Typography sx={{ color: 'white' }}>Cash Payment</Typography>
                        <Typography variant="caption" sx={{ color: '#999' }}>
                          Pay {formatCurrency(calculation.interest)} to lender's bank account
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #333', p: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          sx={{ color: 'white' }}
        >
          Cancel
        </Button>
        {calculation && (
          <Button 
            onClick={handleExecute}
            disabled={loading || success}
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <Calculate />}
            sx={{
              bgcolor: '#FFD700',
              color: '#000',
              '&:hover': { bgcolor: '#FFC700' },
              '&:disabled': { bgcolor: '#555', color: '#999' },
            }}
          >
            {loading ? 'Processing...' : 'Execute Crystallisation'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CrystallisationDialog;
